import {
  Component,
  EventEmitter,
  IterableDiffers,
  Input,
  Output,
} from "@angular/core";
import { BasicList, DualListComponent } from "angular-dual-listbox";

@Component({
  selector: "app-custom-listbox",
  templateUrl: "./custom-listbox.component.html",
  styleUrls: ["./custom-listbox.component.scss"],
})
export class CustomListboxComponent extends DualListComponent {
  @Input() sourceName = "";
  @Input() targetName = "";
  @Input() display: any = "";

  @Output() selectedItemsChange = new EventEmitter();
  @Output() selectChange = new EventEmitter();

  constructor(differs: IterableDiffers) {
    super(differs);
  }

  moveAll(): void {
    this.selectAll(this.available);
    this.moveItem(this.available, this.confirmed);
  }

  removeAll(): void {
    this.selectAll(this.confirmed);
    this.moveItem(this.confirmed, this.available);
  }

  // Override function in DualListComponent to add custom list item label.
  makeName(item: any, separator?: string): string {
    const label = item[this.display[0]];
    return label;
  }

  customMakeUnavailable(source: BasicList, item: any): void {
    const idx = source.list.indexOf(item);
    if (idx !== -1) {
      source.list.splice(idx, 1);
    }
  }

  customTrueUp(): void {
    let changed = false;

    // Clear removed items.
    let pos = this.destination.length;
    while ((pos -= 1) >= 0) {
      const mv = this.confirmed.list.filter((conf) => {
        if (typeof this.destination[pos] === "object") {
          return conf._id === this.destination[pos][this.key];
        } else {
          return conf._id === this.destination[pos];
        }
      });
      if (mv.length === 0) {
        // Not found so remove.
        this.destination.splice(pos, 1);
        changed = true;
      }
    }

    // Push added items.
    for (let i = 0, len = this.confirmed.list.length; i < len; i += 1) {
      let mv = this.destination.filter((d: any) => {
        if (typeof d === "object") {
          return d[this.key] === this.confirmed.list[i]._id;
        } else {
          return d === this.confirmed.list[i]._id;
        }
      });

      if (mv.length === 0) {
        // Not found so add.
        mv = this.source.filter((o: any) => {
          if (typeof o === "object") {
            return o[this.key] === this.confirmed.list[i]._id;
          } else {
            return o === this.confirmed.list[i]._id;
          }
        });

        if (mv.length > 0) {
          this.destination.push(mv[0]);
          changed = true;
        }
      }
    }

    if (changed) {
      this.destinationChange.emit(this.destination);
    }
  }

  moveItem(
    source: BasicList,
    target: BasicList,
    item: any = null,
    trueup = true
  ): void {
    let i = 0;
    let len = source.pick.length;

    if (item) {
      i = source.list.indexOf(item);
      len = i + 1;
    }

    for (; i < len; i += 1) {
      // Is the pick still in list?
      let mv: Array<any> = [];
      if (item) {
        const idx = this.findItemIndex(source.pick, item);
        if (idx !== -1) {
          mv[0] = source.pick[idx];
        }
      } else {
        mv = source.list.filter((src) => {
          return src._id === source.pick[i]._id;
        });
      }

      // Should only ever be 1
      if (mv.length === 1) {
        // Add if not already in target.
        if (target.list.filter((trg) => trg._id === mv[0]._id).length === 0) {
          target.list.push(mv[0]);
          this.selectedItemsChange.next({
            source: source.name,
            intId: mv[0]._id,
          });
        }
        this.customMakeUnavailable(source, mv[0]);
      }
    }

    if (this.compare !== undefined) {
      target.list.sort(this.compare);
    }

    source.pick.length = 0;

    // Update destination
    if (trueup) {
      this.customTrueUp();
    }

    // Delay ever-so-slightly to prevent race condition.
    setTimeout(() => {
      this.onFilter(source);
      this.onFilter(target);
    }, 10);
  }

  buildAvailable(source: Array<any>): boolean {
    const sourceChanges = this.sourceDiffer.diff(source);
    if (sourceChanges) {
      sourceChanges.forEachRemovedItem((r: any) => {
        const idx = this.findItemIndex(this.available.list, r.item, this.key);
        if (idx !== -1) {
          this.available.list.splice(idx, 1);
        }
      });

      sourceChanges.forEachAddedItem((r: any) => {
        // Do not add duplicates even if source has duplicates.
        if (this.findItemIndex(this.available.list, r.item, this.key) === -1) {
          this.available.list.push({
            _id: r.item.id,
            _name: r.item.title,
            _type: r.item.interactionType
          });
        }
      });

      if (this.compare !== undefined) {
        this.available.list.sort(this.compare);
      }
      this.available.sift = this.available.list;

      return true;
    }
    return false;
  }

  buildConfirmed(destination: Array<any>): boolean {
    let moved = false;
    const destChanges = this.destinationDiffer.diff(destination);
    if (destChanges) {
      destChanges.forEachRemovedItem((r: any) => {
        const idx = this.findItemIndex(this.confirmed.list, r.item, this.key);
        if (idx !== -1) {
          if (
            !this.isItemSelected(this.confirmed.pick, this.confirmed.list[idx])
          ) {
            this.selectItem(this.confirmed.pick, this.confirmed.list[idx]);
          }
          this.moveItem(
            this.confirmed,
            this.available,
            this.confirmed.list[idx],
            false
          );
          moved = true;
        }
      });

      destChanges.forEachAddedItem((r: any) => {
        const idx = this.findItemIndex(this.available.list, r.item, this.key);
        if (idx !== -1) {
          if (
            !this.isItemSelected(this.available.pick, this.available.list[idx])
          ) {
            this.selectItem(this.available.pick, this.available.list[idx]);
          }
          this.moveItem(
            this.available,
            this.confirmed,
            this.available.list[idx],
            false
          );
          moved = true;
        }
      });

      if (this.compare !== undefined) {
        this.confirmed.list.sort(this.compare);
      }
      this.confirmed.sift = this.confirmed.list;

      if (moved) {
        this.customTrueUp();
      }
      return true;
    }
    return false;
  }
}
